// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The units a DSA 1.8 vector field is shown in. The DSA keeps its values in
 * the publisher's units (wind in m/s for tropical cyclones), and the style
 * maths runs on those raw values. Only what the viewer reads changes: AYNI
 * shows speeds in km/h (the owner, 2026-10-12), in labels, the inspection
 * panel and the legend alike, so the three always agree.
 */
import type { VectorField } from './vectorTypes'

export interface DisplayUnit {
  /** The units as the viewer reads them. */
  units: string
  /** Shown value = raw value × factor. */
  factor: number
  /** Decimals for the shown value. */
  precision: number
}

/** Speeds the publisher may write, as km/h. */
const TO_KMH: Record<string, number> = {
  'm/s': 3.6,
  'm s-1': 3.6,
  'm s^-1': 3.6,
  kt: 1.852,
  kts: 1.852,
  kn: 1.852,
  knots: 1.852,
  mph: 1.609344,
  'km/h': 1,
}

/** How a field's values are shown; null when they are shown as published. */
export function displayUnitOf(field: VectorField | undefined): DisplayUnit | null {
  if (!field?.units || field.role !== 'attribute') return null
  const factor = TO_KMH[field.units.trim().toLowerCase()]
  if (factor === undefined) return null
  return { units: 'km/h', factor, precision: 0 }
}

/**
 * Publisher text that names a field's units ("…maximum sustained wind, m/s"),
 * with those units as the viewer reads them. Only a whole-word match of the
 * published units is replaced.
 */
export function replaceUnits(text: string, field: VectorField | undefined): string {
  const unit = displayUnitOf(field)
  if (!unit || !field?.units) return text
  const escaped = field.units.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&')
  return text.replace(new RegExp(`(^|[\\s(,])${escaped}(?=$|[\\s),.;])`, 'g'), `$1${unit.units}`)
}
